import { z } from "zod";
/**
 * Stable, client-safe error codes. The API maps internal failures onto these
 * codes; raw database, provider or stack-trace details never cross the wire.
 */
export const ErrorCode = {
    VALIDATION_FAILED: "VALIDATION_FAILED",
    VALIDATION_ERROR: "VALIDATION_ERROR",
    UNAUTHENTICATED: "UNAUTHENTICATED",
    SESSION_EXPIRED: "SESSION_EXPIRED",
    FORBIDDEN: "FORBIDDEN",
    NOT_FOUND: "NOT_FOUND",
    ORGANIZATION_NOT_FOUND: "ORGANIZATION_NOT_FOUND",
    ORGANIZATION_ACCESS_DENIED: "ORGANIZATION_ACCESS_DENIED",
    /** Active membership on a non-deleted organization whose status is SUSPENDED. */
    ORGANIZATION_SUSPENDED: "ORGANIZATION_SUSPENDED",
    SUPER_ADMIN_ACCESS_DENIED: "SUPER_ADMIN_ACCESS_DENIED",
    /** Bootstrap-protected Platform Admin cannot be removed through the API. */
    PLATFORM_ADMIN_PROTECTED: "PLATFORM_ADMIN_PROTECTED",
    /** Removal would leave no active Platform Admin. */
    PLATFORM_ADMIN_LAST: "PLATFORM_ADMIN_LAST",
    PLATFORM_ADMIN_ALREADY_ACTIVE: "PLATFORM_ADMIN_ALREADY_ACTIVE",
    /** No active, non-deleted User with that email. No User is created. */
    PLATFORM_ADMIN_USER_NOT_FOUND: "PLATFORM_ADMIN_USER_NOT_FOUND",
    /**
     * Organization access by a Platform Admin reached an action that edits the
     * caller's own membership (display name). A Platform Admin has none to edit.
     */
    PLATFORM_ADMIN_ACTION_UNAVAILABLE: "PLATFORM_ADMIN_ACTION_UNAVAILABLE",
    /**
     * A Platform Admin action needs the organization's acting OWNER (sends,
     * campaign drafts, Send Test, imports, invitations) and none is active.
     */
    ORGANIZATION_HAS_NO_ACTIVE_OWNER: "ORGANIZATION_HAS_NO_ACTIVE_OWNER",
    CONVERSATION_NOT_FOUND: "CONVERSATION_NOT_FOUND",
    /** Assignee is missing, suspended, deleted, or in another organization. */
    CONVERSATION_ASSIGNEE_NOT_ASSIGNABLE: "CONVERSATION_ASSIGNEE_NOT_ASSIGNABLE",
    /** Mark as Unread found no customer inbound message to hold. */
    CONVERSATION_MARK_UNREAD_NO_INBOUND: "CONVERSATION_MARK_UNREAD_NO_INBOUND",
    /** Conversation export exceeds the spreadsheet row cap. No file is started. */
    CONVERSATION_EXPORT_TOO_LARGE: "CONVERSATION_EXPORT_TOO_LARGE",
    CONTACT_PLATFORM_PROFILE_NOT_FOUND: "CONTACT_PLATFORM_PROFILE_NOT_FOUND",
    NO_CONTACT_TO_RESTORE: "NO_CONTACT_TO_RESTORE",
    CONFLICT: "CONFLICT",
    IDEMPOTENCY_KEY_REUSED: "IDEMPOTENCY_KEY_REUSED",
    FEATURE_DISABLED: "FEATURE_DISABLED",
    /** Feature is Beta ON and the organization is not a Beta organization. */
    BETA_FEATURE_NOT_ENABLED: "BETA_FEATURE_NOT_ENABLED",
    NOT_CONFIGURED: "NOT_CONFIGURED",
    NOT_IMPLEMENTED: "NOT_IMPLEMENTED",
    OAUTH_STATE_INVALID: "OAUTH_STATE_INVALID",
    OAUTH_ACCESS_DENIED: "OAUTH_ACCESS_DENIED",
    OAUTH_EMAIL_NOT_VERIFIED: "OAUTH_EMAIL_NOT_VERIFIED",
    ACCOUNT_DISABLED: "ACCOUNT_DISABLED",
    INVALID_ORIGIN: "INVALID_ORIGIN",
    DEPENDENCY_UNAVAILABLE: "DEPENDENCY_UNAVAILABLE",
    WHATSAPP_ALREADY_CONNECTED: "WHATSAPP_ALREADY_CONNECTED",
    WHATSAPP_NOT_PENDING: "WHATSAPP_NOT_PENDING",
    WHATSAPP_PHONE_SELECTION_INVALID: "WHATSAPP_PHONE_SELECTION_INVALID",
    WHATSAPP_SELECTED_PHONE_NOT_IN_WABA: "WHATSAPP_SELECTED_PHONE_NOT_IN_WABA",
    WHATSAPP_PHONE_ACTIVE_ELSEWHERE: "WHATSAPP_PHONE_ACTIVE_ELSEWHERE",
    WHATSAPP_PHONE_OWNED_BY_ANOTHER_ORGANIZATION: "WHATSAPP_PHONE_OWNED_BY_ANOTHER_ORGANIZATION",
    WHATSAPP_WABA_OWNED_BY_ANOTHER_ORGANIZATION: "WHATSAPP_WABA_OWNED_BY_ANOTHER_ORGANIZATION",
    WHATSAPP_VERIFICATION_ALREADY_COMPLETE: "WHATSAPP_VERIFICATION_ALREADY_COMPLETE",
    WHATSAPP_PHONE_SELECTION_REQUIRED: "WHATSAPP_PHONE_SELECTION_REQUIRED",
    WHATSAPP_WORKSPACE_PHONE_NOT_FOUND: "WHATSAPP_WORKSPACE_PHONE_NOT_FOUND",
    WHATSAPP_WORKSPACE_PHONE_NOT_CONNECTED: "WHATSAPP_WORKSPACE_PHONE_NOT_CONNECTED",
    WHATSAPP_WORKSPACE_PHONE_SELECTION_FAILED: "WHATSAPP_WORKSPACE_PHONE_SELECTION_FAILED",
    WHATSAPP_PHONE_REGISTRATION_REQUIRED: "WHATSAPP_PHONE_REGISTRATION_REQUIRED",
    WHATSAPP_VERIFICATION_REQUEST_FAILED: "WHATSAPP_VERIFICATION_REQUEST_FAILED",
    WHATSAPP_VERIFICATION_RATE_LIMITED: "WHATSAPP_VERIFICATION_RATE_LIMITED",
    WHATSAPP_VERIFICATION_CODE_INVALID: "WHATSAPP_VERIFICATION_CODE_INVALID",
    WHATSAPP_VERIFICATION_CODE_EXPIRED: "WHATSAPP_VERIFICATION_CODE_EXPIRED",
    WHATSAPP_PHONE_NOT_IN_WABA: "WHATSAPP_PHONE_NOT_IN_WABA",
    WHATSAPP_CREDENTIALS_INVALID: "WHATSAPP_CREDENTIALS_INVALID",
    WHATSAPP_REGISTRATION_FAILED: "WHATSAPP_REGISTRATION_FAILED",
    WHATSAPP_REGISTRATION_PENDING: "WHATSAPP_REGISTRATION_PENDING",
    WHATSAPP_PIN_MISMATCH: "WHATSAPP_PIN_MISMATCH",
    WHATSAPP_REGISTRATION_RATE_LIMITED: "WHATSAPP_REGISTRATION_RATE_LIMITED",
    WHATSAPP_DISPLAY_NAME_NOT_APPROVED: "WHATSAPP_DISPLAY_NAME_NOT_APPROVED",
    WHATSAPP_PHONE_PROGRAMMATIC_ADD_NOT_AVAILABLE: "WHATSAPP_PHONE_PROGRAMMATIC_ADD_NOT_AVAILABLE",
    WHATSAPP_PHONE_LIMIT_REACHED: "WHATSAPP_PHONE_LIMIT_REACHED",
    WHATSAPP_PHONE_ALREADY_CONNECTED: "WHATSAPP_PHONE_ALREADY_CONNECTED",
    WHATSAPP_PHONE_ALREADY_EXISTS: "WHATSAPP_PHONE_ALREADY_EXISTS",
    WHATSAPP_EMBEDDED_SIGNUP_VERIFICATION_INCOMPLETE: "WHATSAPP_EMBEDDED_SIGNUP_VERIFICATION_INCOMPLETE",
    WHATSAPP_PROVIDER_ERROR: "WHATSAPP_PROVIDER_ERROR",
    WHATSAPP_NOT_CONNECTED: "WHATSAPP_NOT_CONNECTED",
    WHATSAPP_CUSTOMER_SERVICE_WINDOW_CLOSED: "WHATSAPP_CUSTOMER_SERVICE_WINDOW_CLOSED",
    WHATSAPP_TEMPLATE_NOT_FOUND: "WHATSAPP_TEMPLATE_NOT_FOUND",
    WHATSAPP_TEMPLATE_NOT_ELIGIBLE: "WHATSAPP_TEMPLATE_NOT_ELIGIBLE",
    WHATSAPP_TEMPLATE_UNSUPPORTED: "WHATSAPP_TEMPLATE_UNSUPPORTED",
    WHATSAPP_TEMPLATE_DUPLICATE: "WHATSAPP_TEMPLATE_DUPLICATE",
    WHATSAPP_TEMPLATE_NOT_EDITABLE: "WHATSAPP_TEMPLATE_NOT_EDITABLE",
    WHATSAPP_TEMPLATE_NOT_DELETABLE: "WHATSAPP_TEMPLATE_NOT_DELETABLE",
    WHATSAPP_TEMPLATE_INVALID_PHONE_NUMBER: "WHATSAPP_TEMPLATE_INVALID_PHONE_NUMBER",
    WHATSAPP_TEMPLATE_CREATE_OUTCOME_UNKNOWN: "WHATSAPP_TEMPLATE_CREATE_OUTCOME_UNKNOWN",
    WHATSAPP_TEMPLATE_HEADER_IMAGE_REQUIRED: "WHATSAPP_TEMPLATE_HEADER_IMAGE_REQUIRED",
    WHATSAPP_BUSINESS_PROFILE_UPDATE_FAILED: "WHATSAPP_BUSINESS_PROFILE_UPDATE_FAILED",
    WHATSAPP_CALLING_NOT_ENABLED: "WHATSAPP_CALLING_NOT_ENABLED",
    FACEBOOK_ALREADY_CONNECTED: "FACEBOOK_ALREADY_CONNECTED",
    FACEBOOK_NOT_PENDING: "FACEBOOK_NOT_PENDING",
    FACEBOOK_PAGE_SELECTION_INVALID: "FACEBOOK_PAGE_SELECTION_INVALID",
    FACEBOOK_PAGE_OWNED_BY_ANOTHER_ORGANIZATION: "FACEBOOK_PAGE_OWNED_BY_ANOTHER_ORGANIZATION",
    FACEBOOK_PROVIDER_ERROR: "FACEBOOK_PROVIDER_ERROR",
    FACEBOOK_NOT_CONNECTED: "FACEBOOK_NOT_CONNECTED",
    FACEBOOK_MESSENGER_WINDOW_CLOSED: "FACEBOOK_MESSENGER_WINDOW_CLOSED",
    INSTAGRAM_ALREADY_CONNECTED: "INSTAGRAM_ALREADY_CONNECTED",
    INSTAGRAM_ACCOUNT_UNSUPPORTED: "INSTAGRAM_ACCOUNT_UNSUPPORTED",
    INSTAGRAM_ACCOUNT_OWNED_BY_ANOTHER_ORGANIZATION: "INSTAGRAM_ACCOUNT_OWNED_BY_ANOTHER_ORGANIZATION",
    INSTAGRAM_PROVIDER_ERROR: "INSTAGRAM_PROVIDER_ERROR",
    INSTAGRAM_NOT_CONNECTED: "INSTAGRAM_NOT_CONNECTED",
    INSTAGRAM_MESSAGING_WINDOW_CLOSED: "INSTAGRAM_MESSAGING_WINDOW_CLOSED",
    CONTACT_DUPLICATE_PHONE: "CONTACT_DUPLICATE_PHONE",
    CONTACT_DUPLICATE_EMAIL: "CONTACT_DUPLICATE_EMAIL",
    CONTACT_RESTORE_PHONE_CONFLICT: "CONTACT_RESTORE_PHONE_CONFLICT",
    CONTACT_PHONE_RESTORE_AMBIGUOUS: "CONTACT_PHONE_RESTORE_AMBIGUOUS",
    CONTACT_INVALID_PHONE: "CONTACT_INVALID_PHONE",
    CONTACT_PHONE_MISSING_COUNTRY: "CONTACT_PHONE_MISSING_COUNTRY",
    CONTACT_WHATSAPP_PHONE_LOCKED: "CONTACT_WHATSAPP_PHONE_LOCKED",
    CONTACT_WHATSAPP_PHONE_CONFLICT: "CONTACT_WHATSAPP_PHONE_CONFLICT",
    CONTACT_PLATFORM_PROFILE_CONFLICT: "CONTACT_PLATFORM_PROFILE_CONFLICT",
    CONTACT_MERGE_CONFLICT: "CONTACT_MERGE_CONFLICT",
    CONTACT_MERGE_BIRTHDAY_CHOICE_REQUIRED: "CONTACT_MERGE_BIRTHDAY_CHOICE_REQUIRED",
    CONTACT_INVALID_BIRTHDAY: "CONTACT_INVALID_BIRTHDAY",
    CONTACT_ALREADY_LINKED: "CONTACT_ALREADY_LINKED",
    CONTACT_IMPORT_TOO_MANY_ROWS: "CONTACT_IMPORT_TOO_MANY_ROWS",
    CONTACT_IMPORT_INVALID_MAPPING: "CONTACT_IMPORT_INVALID_MAPPING",
    CONTACT_IMPORT_EMPTY: "CONTACT_IMPORT_EMPTY",
    CONTACT_IMPORT_INVALID_GROUPS: "CONTACT_IMPORT_INVALID_GROUPS",
    CONTACT_IMPORT_FILE_TOO_LARGE: "CONTACT_IMPORT_FILE_TOO_LARGE",
    CONTACT_IMPORT_INVALID_FILE_TYPE: "CONTACT_IMPORT_INVALID_FILE_TYPE",
    CONTACT_IMPORT_JOB_NOT_FOUND: "CONTACT_IMPORT_JOB_NOT_FOUND",
    CONTACT_IMPORT_JOB_NOT_CONFIRMABLE: "CONTACT_IMPORT_JOB_NOT_CONFIRMABLE",
    CONTACT_IMPORT_JOB_EXPIRED: "CONTACT_IMPORT_JOB_EXPIRED",
    CONTACT_IMPORT_REPORT_EXPIRED: "CONTACT_IMPORT_REPORT_EXPIRED",
    CONTACT_IMPORT_REPORT_UNAVAILABLE: "CONTACT_IMPORT_REPORT_UNAVAILABLE",
    OBJECT_STORAGE_UNAVAILABLE: "OBJECT_STORAGE_UNAVAILABLE",
    OBJECT_NOT_FOUND: "OBJECT_NOT_FOUND",
    ATTACHMENT_NOT_AVAILABLE: "ATTACHMENT_NOT_AVAILABLE",
    ATTACHMENT_EXPIRED: "ATTACHMENT_EXPIRED",
    STORAGE_QUOTA_EXCEEDED: "STORAGE_QUOTA_EXCEEDED",
    CONTACT_LIMIT_REACHED: "CONTACT_LIMIT_REACHED",
    TEAM_MEMBER_LIMIT_REACHED: "TEAM_MEMBER_LIMIT_REACHED",
    PLATFORM_NOT_ENABLED: "PLATFORM_NOT_ENABLED",
    ORGANIZATION_ENTITLEMENT_NOT_CONFIGURED: "ORGANIZATION_ENTITLEMENT_NOT_CONFIGURED",
    MESSAGE_BALANCE_NOT_CONFIGURED: "MESSAGE_BALANCE_NOT_CONFIGURED",
    MESSAGE_BALANCE_MODE_CHANGE_BLOCKED: "MESSAGE_BALANCE_MODE_CHANGE_BLOCKED",
    MESSAGE_BALANCE_VERSION_CONFLICT: "MESSAGE_BALANCE_VERSION_CONFLICT",
    MESSAGE_BALANCE_OPERATION_INVALID: "MESSAGE_BALANCE_OPERATION_INVALID",
    MESSAGE_BALANCE_BLOCKED_JOBS_CHANGED: "MESSAGE_BALANCE_BLOCKED_JOBS_CHANGED",
    INSUFFICIENT_MESSAGE_CREDITS: "INSUFFICIENT_MESSAGE_CREDITS",
    MESSAGE_CREDIT_INTEGRITY_ERROR: "MESSAGE_CREDIT_INTEGRITY_ERROR",
    CAMPAIGN_DELETE_NOT_ALLOWED: "CAMPAIGN_DELETE_NOT_ALLOWED",
    INTERNAL: "INTERNAL",
    INTERNAL_ERROR: "INTERNAL_ERROR",
};
export const apiErrorResponseSchema = z.object({
    error: z
        .object({
        code: z.string(),
        message: z.string(),
    })
        .catchall(z.union([z.string(), z.number(), z.boolean(), z.null()])),
    requestId: z.string().optional(),
});
//# sourceMappingURL=errors.js.map